;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.4";import * as Sentry from '@sentry/nextjs'

const dsn = process.env.SENTRY_DSN
const skipSentry = process.env.SKIP_SENTRY === 'true'

if (dsn && !skipSentry) {
	Sentry.init({
		dsn,
		enabled: process.env.NODE_ENV === 'production',
		release: process.env.VERCEL_GIT_COMMIT_SHA || process.env.VERCEL_GITHUB_COMMIT_SHA,
		environment: process.env.NEXT_PUBLIC_AUDUR_ENV,
		tracesSampleRate: 0.1,
		sendDefaultPii: false,
		ignoreErrors: [
			// Generic browser noise
			/^Script error\.?$/,
			/^ResizeObserver loop/,
			// Transient network failures — not actionable
			'Failed to fetch',
			'Load failed',
			'NetworkError',
			'ChunkLoadError',
			// Google Tag Manager / Analytics blocked by adblockers — outside our control
			/Failed to load.*googletagmanager\.com/,
			/Load failed.*googletagmanager\.com/,
			/^Failed to load https:\/\/(?:www\.)?google-analytics\.com/,
			// iOS-only native bridge invoked from regular browser context (webview probes)
			/window\.webkit\.messageHandlers/,
			// reCAPTCHA — 3rd party timeouts and missing config on pages that don't use it
			/Non-Error promise rejection captured with value: Timeout/,
			/reCAPTCHA Timeout/,
			// Browser extension messages leaking via runtime.sendMessage
			'Invalid call to runtime.sendMessage(). Tab not found.',
			// MetaMask / wallet extension noise from inpage.js
			'func sseError not found',
			// Next.js navigation aborts — user navigated away before route loaded
			'Cancel rendering route',
			/Abort fetching component for route/,
			/Route did not complete loading/,
			// Browser feature-detection extensions injecting bogus probes
			/^Error: feature named `\w+` was not found$/,
			// MS Office/Teams-style embedded-webview messaging noise
			/Object Not Found Matching Id/,
			// Facebook/Instagram in-app browser tearing down its WKWebView mid-navigation
			'The WKWebView was deallocated before the message was delivered',
		],
		denyUrls: [/chrome-extension:\/\//, /moz-extension:\/\//, /safari-extension:\/\//, /safari-web-extension:\/\//],
		beforeSend(event, hint) {
			const error = hint?.originalException
			const message = (error && (error.message || error.toString())) || event.message || ''

			const isAnalyticsFile = (str) =>
				!!str &&
				/(googletagmanager|google-analytics|gtag|analytics\.js|segment|amplitude|facebook|fbq|adsbygoogle)/i.test(str)

			const isBlockedByClient =
				/net::ERR_BLOCKED_BY_CLIENT/i.test(message) ||
				/Refused to connect to/i.test(message) ||
				/Script error\./i.test(message) ||
				/Failed to load/i.test(message) ||
				/is not defined/i.test(message)

			const stackHasAnalyticsFile = event.exception?.values?.some((v) =>
				v.stacktrace?.frames?.some((f) => isAnalyticsFile(f.filename))
			)

			const isLikelyAdblockIssue =
				(isBlockedByClient && stackHasAnalyticsFile) || (isBlockedByClient && isAnalyticsFile(message))

			if (isLikelyAdblockIssue) {
				return null
			}

			if (
				message.includes('injection-tss') ||
				message.includes('TSS:') ||
				message.includes('checkDomainAndRun') ||
				message.includes('chrome-extension') ||
				message.includes('content-script')
			) {
				return null
			}

			if (event.request?.url?.includes('chrome-extension://') || event.culprit?.includes('injection-tss')) {
				return null
			}

			// iOS WKWebView bridge API — only exists inside the native iOS app shell.
			if (message.includes('window.webkit.messageHandlers')) {
				return null
			}

			// Android WebView Java bridge lifecycle — OS GC's the Java object mid-use.
			if (
				message.includes('Java object is gone') ||
				message.includes('Java bridge method invocation error') ||
				message.includes('Error invoking postMessage: Java')
			) {
				return null
			}

			// Chrome extension runtime.sendMessage noise from user-installed extensions.
			if (message.includes('Tab not found') || message.includes('runtime.sendMessage')) {
				return null
			}

			// Browser storage blocked by privacy settings (private mode / strict tracking protection).
			if (message.includes('The operation is insecure') || message.includes('Failed to read localStorage')) {
				return null
			}

			// Sentry's own XHR instrumentation race condition.
			if (message.includes('__complete')) {
				return null
			}

			// Chrome-on-iOS WebKit-injected "Maximum call stack size exceeded" noise.
			// Four-signal guard preserves real app recursion bugs.
			const frames = event.exception?.values?.[0]?.stacktrace?.frames || []
			const ex = event.exception?.values?.[0]
			if (
				ex?.type === 'RangeError' &&
				ex?.value?.includes('Maximum call stack size exceeded') &&
				ex?.mechanism?.type === 'onerror' &&
				!frames.some((f) => f.in_app)
			) {
				return null
			}

			// Samsung Internet / in-app WebView browsers inject globals (`xbrowser`,
			// `swbrowser`) for features that don't exist in this context.
			if (/\b(xbrowser|swbrowser)\b/.test(message)) {
				return null
			}

			// Same WebKit-injected-script family as the RangeError guard above, just a
			// different symptom: a parse error or an opaque 1-3 char error thrown from
			// a script with no app frames at all (not even an unresolved sourcemap).
			if (!frames.some((f) => f.in_app) && ex?.mechanism?.type === 'onerror') {
				if (ex?.type === 'SyntaxError') {
					return null
				}
				if (/^[A-Za-z]{1,3}$/.test(ex?.value || '')) {
					return null
				}
			}

			// Browser extensions that JSON.stringify() a DOM node still carrying a
			// React fiber reference — the circularity is in the extension's own code.
			if (message.includes('Converting circular structure to JSON') && !frames.some((f) => f.in_app)) {
				return null
			}

			// Scrub auth-bearing headers from any error-derived context. Prismic API
			// calls in getStaticProps carry a `Authorization: Token <PRISMIC_API_TOKEN>`
			// header — if axios attaches it via extraErrorDataIntegration in any
			// future change, this loop catches it. Defensive only — the current
			// config does not register extraErrorDataIntegration.
			const SENSITIVE_HEADER = /^(authorization|x-authorization-app|cookie|set-cookie|proxy-authorization)$/i
			Object.values(event?.contexts || {}).forEach((ctx) => {
				const headers = ctx && ctx.config && ctx.config.headers
				if (headers) {
					Object.keys(headers).forEach((key) => {
						if (SENSITIVE_HEADER.test(key)) headers[key] = '[REDACTED]'
					})
				}
			})

			return event
		},
	})
}

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart
